import {
  CreateFormResponse,
  Criterion,
  FormResponse,
  Participant,
  useParticipantFields,
} from '@app/api';
import { useGetParticipantDisplayText } from '@app/participant-fields';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBuildAnswerForParticipantField } from './lib/buildAnswerForParticipantField';
import { useFieldChoice } from './useFieldChoice';

const createScreenerStorageItem = (screenerFormId: string) =>
  `SCREENER_RESPONSE_${screenerFormId}`;
const createProfileStorageItem = (screenerFormId: string) =>
  `PROFILE_${screenerFormId}`;

export const useBuildCreateFormResponse = ({
  screenerFormId,
  startedAt,
  criteria,
}: {
  screenerFormId?: string;
  startedAt: Date;
  criteria: Criterion[];
}) => {
  const buildAnswerForParticipantField = useBuildAnswerForParticipantField();

  const initialScreenerResponse = useMemo(() => {
    if (!screenerFormId) {
      return undefined;
    }
    const item = sessionStorage.getItem(
      createScreenerStorageItem(screenerFormId),
    );
    if (item) {
      return JSON.parse(item);
    }
    return undefined;
  }, [screenerFormId]);

  const initialProfile = useMemo(() => {
    if (!screenerFormId) {
      return undefined;
    }
    let item = sessionStorage.getItem(createProfileStorageItem(screenerFormId));
    if (!item) {
      item = sessionStorage.getItem(createProfileStorageItem('DEFAULT'));
    }
    if (item) {
      return JSON.parse(item);
    }
    return undefined;
  }, [screenerFormId]);

  const [screenerResponse, setScreenerResponse] = useState<FormResponse>(
    initialScreenerResponse,
  );
  const [profile, setProfile] = useState<Partial<Participant>>(initialProfile);
  const { t } = useTranslation();

  const { value: deviceChoice, setValue: setDeviceChoice } = useFieldChoice(
    screenerFormId,
    'DEVICE_CHOICE',
  );
  const { value: jobChoice, setValue: setJobChoice } = useFieldChoice(
    screenerFormId,
    'JOB_CHOICE',
  );

  const { data: fields = [] } = useParticipantFields();
  const getParticipantDisplayValue = useGetParticipantDisplayText();

  const buildCreateFormResponse = useCallback(
    (responseParam?: FormResponse, profileParam?: Partial<Participant>) => {
      let formResponse: CreateFormResponse | undefined;
      if (responseParam || screenerResponse) {
        formResponse = {
          answers: responseParam?.answers || screenerResponse?.answers || [],
          startedAt,
          formId: screenerFormId,
        };
      }

      if (profile || profileParam) {
        if (!formResponse) {
          formResponse = {
            answers: [],
            startedAt,
            formId: screenerFormId,
          };
        }

        for (const field of fields) {
          const criterion = criteria.find((c) => c.fieldRef === field.name);
          if (!criterion) {
            continue;
          }
          const answer = buildAnswerForParticipantField(
            field,
            profileParam || profile,
            criterion,
          );
          if (answer) {
            formResponse.answers.push(answer);
          }
        }
      }

      if (deviceChoice) {
        if (!formResponse) {
          formResponse = {
            answers: [],
            startedAt,
            formId: screenerFormId,
          };
        }
        formResponse.answers.push(deviceChoice);
      }

      if (jobChoice) {
        if (!formResponse) {
          formResponse = {
            answers: [],
            startedAt,
            formId: screenerFormId,
          };
        }
        formResponse.answers.push(jobChoice);
      }

      if (!formResponse?.answers?.length) {
        return undefined;
      }

      return formResponse;
    },
    [
      screenerFormId,
      startedAt,
      screenerResponse,
      profile,
      deviceChoice,
      jobChoice,
      t,
      fields,
      getParticipantDisplayValue,
      criteria,
      buildAnswerForParticipantField,
    ],
  );

  const saveFormResponse = useCallback(
    (screenerResponse: FormResponse) => {
      sessionStorage.setItem(
        createScreenerStorageItem(screenerFormId!),
        JSON.stringify(screenerResponse),
      );
      setScreenerResponse(screenerResponse);
    },
    [screenerFormId, setScreenerResponse],
  );

  const saveProfile = useCallback(
    (profile: Partial<Participant>) => {
      if (!screenerFormId) {
        sessionStorage.setItem(
          createProfileStorageItem('DEFAUlT'!),
          JSON.stringify(profile),
        );
      } else {
        sessionStorage.setItem(
          createProfileStorageItem(screenerFormId!),
          JSON.stringify(profile),
        );
      }
      setProfile(profile);
    },
    [screenerFormId, setProfile],
  );

  return {
    buildCreateFormResponse,
    setScreenerResponse: saveFormResponse,
    setProfile: saveProfile,
    setDeviceChoice,
    setJobChoice,
    profile,
  };
};
